import {
	isSame,
	clone,
	getDiff,
	parseDate,
	localOrUtcTimestampToLocalDate,
} from "@sembark-travel/datetime-utils"
import type {
	IQuote,
	ITrip,
	IQuoteCab,
	IQuoteHotel,
	ITripQuote,
	TComponentKey,
	TGivenQuotePricingStrategy,
	IGivenQuote,
	TTaxAppliedOn,
	TPricePerTravelComponentAllocation,
	TUnroundedGivenPrice,
	TPricePerPersonAllocation,
} from "./store"
import { collect } from "../utils"
import { type TChildrenArray, childrenToQueryParams } from "./../Tourists"
import {
	addMoneyFromDifferentCurrencies,
	formatMoneyByDecimal,
	getMoneyRatio,
	makeCurrencyConverter,
	moneyParseByDecimal,
	subtractMoney,
	multipleMoneyBy,
	TMoney,
	addMoney,
	createMoney,
	divideMoneyBy,
} from "@sembark-travel/money"
import { normalizeNumber } from "@sembark-travel/number-utils"
import { Required } from "utility-types"
import { isTruthy, useFieldValue } from "@sembark-travel/ui-form"
import { useEffect, useMemo } from "react"
import pluralize from "pluralize"
import { allocatePaxInHotels } from "../HotelPrices"
import { processVisasServiceForPax } from "../VisaServicePrices"

type TDiff = {
	message: string
	startDate?: {
		trip: Date
		quote: Date
	}
	days?: {
		trip: number
		quote: number
	}
	noOfAdults?: {
		trip: number
		quote: number
	}
	children?: {
		trip: TChildrenArray
		quote: TChildrenArray
	}
	complimentary_count?: {
		trip: number
		quote: number
	}
}
/**
 * Get the diff between a trip and it's quote's basic details
 */
export function getTripQuoteDetailsDiff(
	trip: Pick<
		ITrip,
		| "start_date"
		| "start_date_local"
		| "days"
		| "no_of_adults"
		| "children"
		| "complimentary_count"
	>,
	quote: Pick<
		IQuote,
		| "start_date"
		| "start_date_local"
		| "days"
		| "no_of_adults"
		| "children"
		| "complimentary_count"
	>
): TDiff | null {
	const startDate = localOrUtcTimestampToLocalDate(
		quote.start_date_local,
		quote.start_date
	)
	const { days, children, no_of_adults, complimentary_count } = quote
	const diff: TDiff = {
		message: "",
	}
	if (
		!isSame(
			startDate,
			localOrUtcTimestampToLocalDate(trip.start_date_local, trip.start_date)
		)
	) {
		diff.startDate = {
			trip: localOrUtcTimestampToLocalDate(
				trip.start_date_local,
				trip.start_date
			),
			quote: startDate,
		}
	}
	if (days !== trip.days) {
		diff.days = {
			trip: trip.days,
			quote: days,
		}
	}
	if (no_of_adults !== trip.no_of_adults) {
		diff.noOfAdults = {
			trip: trip.no_of_adults,
			quote: no_of_adults,
		}
	}
	if (
		childrenToQueryParams(children) !== childrenToQueryParams(trip.children)
	) {
		diff.children = {
			trip: trip.children,
			quote: children,
		}
	}
	if (complimentary_count !== trip.complimentary_count) {
		diff.complimentary_count = {
			trip: trip.complimentary_count,
			quote: complimentary_count,
		}
	}
	const notMatched = []
	if (diff.startDate) {
		notMatched.push("Start Date")
	}
	if (diff.days) {
		notMatched.push("Duration")
	}
	if (diff.noOfAdults) {
		notMatched.push("Number of Adults")
	}
	if (diff.children) {
		notMatched.push("Children")
	}

	if (diff.complimentary_count) {
		notMatched.push("Total FOC")
	}

	if (notMatched.length > 0) {
		diff.message = notMatched.join(", ") + " don't match"
		return diff
	}
	return null
}

export function getUniqueHotels(
	hotels: Array<IQuoteHotel>,
	config?: { includePrice: boolean }
) {
	const stay_hotels = hotels.reduce<Array<Array<IQuoteHotel>>>(
		(stay_hotels, hotel) => {
			if (!stay_hotels.length) return [[hotel]]
			const index = stay_hotels.length - 1
			const last_hotel = stay_hotels[index][stay_hotels[index].length - 1]
			if (
				last_hotel.hotel.id === hotel.hotel.id &&
				Math.abs(getDiff(hotel.date, last_hotel.date, "days")) <= 1
			) {
				stay_hotels[index].push(hotel)
			} else {
				stay_hotels.push([hotel])
			}
			return stay_hotels
		},
		[]
	)
	const uniqueHotels = stay_hotels.map((quoteHotels) => {
		const nights = quoteHotels.reduce<
			Array<{
				night: Date
				quoteHotel: IQuoteHotel
			}>
		>((nights, quoteHotel) => {
			const night = localOrUtcTimestampToLocalDate(
				quoteHotel.date_local,
				quoteHotel.date
			)
			nights.push({
				night: clone(night),
				quoteHotel,
			})
			return nights
		}, [])
		const roomsAndMealPlansById = nights.reduce<{
			[key: string]: Array<{
				night: Date
				quoteHotel: IQuoteHotel
				price:
					| undefined
					| {
							currency?: string
							given_price?: number
							per_room_given_price?: number
							per_adult_with_extra_bed_given_price?: number
							per_child_with_extra_bed_given_price?: number
							per_child_without_extra_bed_given_price?: number
					  }
			}>
		}>((roomsAndMealPlans, { night, quoteHotel }) => {
			const {
				room_type,
				meal_plan,
				adults_with_extra_bed,
				children_with_extra_bed,
				children_without_extra_bed,
				no_of_rooms,
				persons_per_room,
				similar_hotel_options,
				currency,
				given_price,
				per_room_given_price,
				per_adult_with_extra_bed_given_price,
				per_child_with_extra_bed_given_price,
				per_child_without_extra_bed_given_price,
			} = quoteHotel
			let price
			if (config?.includePrice) {
				price = {
					currency,
					given_price,
					per_room_given_price,
					per_adult_with_extra_bed_given_price,
					per_child_with_extra_bed_given_price,
					per_child_without_extra_bed_given_price,
				}
				if (similar_hotel_options?.length) {
					// check if any similar hotel has more price then the main hotel
					// and update if any
					const similar_hotel_options_prices = similar_hotel_options.map(
						({
							currency,
							given_price,
							per_room_given_price,
							per_adult_with_extra_bed_given_price,
							per_child_with_extra_bed_given_price,
							per_child_without_extra_bed_given_price,
						}) => ({
							currency,
							given_price,
							per_room_given_price,
							per_adult_with_extra_bed_given_price,
							per_child_with_extra_bed_given_price,
							per_child_without_extra_bed_given_price,
						})
					)
					for (const sp of similar_hotel_options_prices) {
						if (Number(sp.given_price || 0) > Number(price.given_price || 0)) {
							price = sp
						}
					}
				}
			}
			const id = [
				room_type.id,
				meal_plan.id,
				adults_with_extra_bed,
				children_with_extra_bed,
				children_without_extra_bed,
				no_of_rooms,
				persons_per_room,
				similar_hotel_options?.length || 0,
			]
				.concat(
					config?.includePrice && price
						? [
								price.given_price,
								price.per_room_given_price || 0,
								price.per_adult_with_extra_bed_given_price || 0,
								price.per_child_with_extra_bed_given_price || 0,
								price.per_child_without_extra_bed_given_price || 0,
							]
						: []
				)
				.join("-")
			if (!roomsAndMealPlans[id]) {
				roomsAndMealPlans[id] = []
			}
			roomsAndMealPlans[id].push({ night, quoteHotel, price })
			return roomsAndMealPlans
		}, {})
		const roomsAndMealPlans = Object.keys(roomsAndMealPlansById).map((id) => {
			const nights = roomsAndMealPlansById[id]
			const quoteHotel = nights[0].quoteHotel
			const price = nights[0].price
			const {
				room_type,
				meal_plan,
				adults_with_extra_bed,
				children_with_extra_bed,
				children_without_extra_bed,
				no_of_rooms,
				persons_per_room,
				room_configuration,
			} = quoteHotel

			return {
				nights: nights.map((n) => n.night),
				room_type,
				meal_plan,
				adults_with_extra_bed,
				children_with_extra_bed,
				children_without_extra_bed,
				no_of_rooms,
				persons_per_room,
				room_configuration,
				...(price || {}),
			}
		})
		return {
			hotel: quoteHotels[0].hotel,
			similar_hotel_options: quoteHotels[0].similar_hotel_options,
			nights: nights
				.map((n) => n.night)
				.reduce<Array<Date>>((uniqueNights, night) => {
					const exists = uniqueNights.findIndex((n) => isSame(n, night, "date"))
					if (exists === -1) {
						uniqueNights.push(night)
					}
					return uniqueNights
				}, []),
			roomsAndMealPlans,
		}
	})
	uniqueHotels.sort((a, b) => (a.nights[0] > b.nights[0] ? 1 : -1))
	return uniqueHotels
}

export function getCabsWithLocations(cabs: Array<IQuoteCab>) {
	const byCabTypeId = cabs.reduce<{ [key: string]: Array<IQuoteCab> }>(
		(byCabTypeId, cab) => {
			const key = cab.cab_type.id + cab.no_of_cabs
			if (!byCabTypeId[key]) {
				byCabTypeId[key] = [cab]
			} else {
				byCabTypeId[key].push(cab)
			}
			return byCabTypeId
		},
		{}
	)
	const cabTypes = collect(
		Object.keys(byCabTypeId).map((cabTypeId: string) => {
			const quoteCabs = byCabTypeId[cabTypeId]
			const dateServices = quoteCabs.map(
				(h) => h.date + "_" + h.transport_service.id
			)
			dateServices.sort()
			const dateServiceId = dateServices.join("-")
			return {
				dateServiceId: dateServiceId,
				cabType: quoteCabs[0].cab_type,
				noOfCabs: quoteCabs[0].no_of_cabs,
				services: quoteCabs.map((h) => h.transport_service),
				dates: quoteCabs.map((h) => h.date),
				dateWiseServices: quoteCabs,
			}
		})
	)
		.groupBy((item) => item.dateServiceId)
		.toArray()
		.map((grouped_by_services) => {
			return {
				dates: grouped_by_services[0]["dates"],
				services: grouped_by_services[0]["services"],
				dateWiseServices: grouped_by_services[0]["dateWiseServices"],
				cabs: grouped_by_services.map(function (s) {
					return {
						cabType: s["cabType"],
						noOfCabs: s["noOfCabs"],
					}
				}),
				cabsStr: grouped_by_services
					.map(function (s) {
						return s["noOfCabs"] + " - " + s["cabType"].name
					})
					.join(" + "),
			}
		})
	// sort the cab types so that the first one is the one that is provided on minimum services
	cabTypes.sort((a, b) => a.services.length - b.services.length)
	return cabTypes
}

export function calculateTotalPrice({
	costPriceCurrency,
	costPriceWithoutFlights,
	flightPrice,
	gstIncluded,
	taxPercentage,
	margin,
}: {
	costPriceCurrency: string
	costPriceWithoutFlights: number
	flightPrice: number | undefined
	gstIncluded: boolean
	taxPercentage: number | null | undefined
	margin: number | null | undefined
}): number {
	const tax = gstIncluded && taxPercentage ? taxPercentage : 0
	const priceWithoutTax = Number(costPriceWithoutFlights) + Number(margin || 0)
	// given_price = totalCostPrice + margin + (costPriceWithoutFlights + margin) * tax / 100
	return Number(
		formatMoneyByDecimal(
			moneyParseByDecimal(
				priceWithoutTax +
					(priceWithoutTax * Number(tax)) / 100 +
					Number(flightPrice || 0),
				costPriceCurrency
			)
		)
	)
}

export function calculateMarginFromTotalPrice({
	givenPrice,
	costPriceWithoutFlights,
	flightPrice,
	gstIncluded,
	taxPercentage,
}: {
	givenPrice: number
	costPriceWithoutFlights: number
	flightPrice?: number
	gstIncluded: boolean
	taxPercentage?: number | null
}): number {
	const tax = gstIncluded && taxPercentage ? taxPercentage : 0
	// given_price = costPriceWithoutFlights + flightPrice + margin + (costPriceWithoutFlights + margin) * tax / 100
	// margin = (given_price - costPriceWithoutFlights - flightPrice - costPriceWithoutFlights * tax / 100) / (1 + tax / 100)
	// margin = (100 * (given_price - costPriceWithoutFlights - flightPrice) - costPriceWithoutFlights * tax) / (100 + tax)
	return Math.ceil(
		(100 * (givenPrice - costPriceWithoutFlights - (flightPrice || 0)) -
			costPriceWithoutFlights * tax) /
			(100 + tax)
	)
}

export function calculateMarginPercentageFromTotalPrice({
	margin,
	costPriceWithoutFlights,
}: {
	margin: number
	costPriceWithoutFlights: number
}): number {
	if (Number(costPriceWithoutFlights || 0) <= 0) return 0
	if (Number(margin) <= 0) return 0
	return normalizeNumber(
		(Number(margin || 0) * 100) / Number(costPriceWithoutFlights || 1),
		4
	)
}

export function getDaywiseTransportsAndActivitiesForQuote({
	transports,
	transportExtras,
	travelActivities,
}: {
	transports: Array<IQuote["cabs"][number] & { fetching_prices?: boolean }>
	transportExtras: IQuote["transport_extras"]
	travelActivities: IQuote["travel_activities"]
}) {
	const daywiseTransportServices = collect(transports)
		.groupBy((t) => `${t.group_id || "."}-${t.date}`)
		.toArray()
		.map((transports) => {
			const date = transports[0].date
			const date_local = transports[0].date_local
			return {
				date,
				date_local,
				transports: collect(transports)
					.groupBy((t) => t.transport_service.id)
					.toArray()
					.map((services) => {
						return {
							id: services[0].id,
							transport_service: services[0].transport_service,
							start_time_formatted: services[0].start_time_formatted,
							duration_string: services[0].duration_formatted,
							sort_order: services[0].sort_order,
							fetching_prices: services.some((s) => Boolean(s.fetching_prices)),
							cabs: services,
						}
					}),
			}
		})
	const daywiseTransportExtraServices = collect(
		transportExtras.filter(
			(service): service is Required<typeof service, "date"> =>
				Boolean(service.date)
		)
	)
		.groupBy((t) => t.date)
		.toArray()
		.map((extras) => {
			const date = extras[0].date
			const date_local = extras[0].date_local
			return {
				date,
				date_local,
				extras,
			}
		})
	const daywiseActivityServices = collect(travelActivities || [])
		.groupBy((t) => `${t.group_id || "."}-${t.date}`)
		.toArray()
		.map((activities) => {
			const date = activities[0].date
			const date_local = activities[0].date_local
			return {
				date,
				date_local,
				activities,
			}
		})
	const daywiseServices = collect(
		(
			[] as Array<{
				date: string
				date_local: string | undefined
				transports?: (typeof daywiseTransportServices)[0]["transports"]
				activities?: (typeof daywiseActivityServices)[0]["activities"]
				extras?: (typeof daywiseTransportExtraServices)[0]["extras"]
			}>
		)
			.concat(daywiseTransportServices)
			.concat(daywiseActivityServices)
			.concat(daywiseTransportExtraServices)
	)
		.groupBy((service) => service.date)
		.toArray()
		.map((daywiseServices) => {
			let activitiesNotAttachedtoCabs = daywiseServices.flatMap(
				(s) => s.activities || []
			)
			const transports = daywiseServices
				.flatMap((s) => s.transports || [])
				.map((transport) => {
					const [activitiesAttachedToThisTransport, remainingActivities] =
						collect(activitiesNotAttachedtoCabs).partition(
							(a): a is Required<typeof a, "quote_cab_id"> =>
								a.quote_cab_id === transport.id
						)
					activitiesNotAttachedtoCabs = remainingActivities
					return {
						...transport,
						activities: activitiesAttachedToThisTransport,
					}
				})
			const extras = daywiseServices.flatMap((s) => s.extras || [])
			return {
				date: daywiseServices[0].date,
				date_local: daywiseServices[0].date_local,
				transports,
				activities: activitiesNotAttachedtoCabs,
				extras,
			}
		})

	daywiseServices.sort((a, b) =>
		a.date > b.date ? 1 : a.date === b.date ? 0 : -1
	)
	const extraServiceWithoutDates = transportExtras.filter((t) => !t.date)
	const daywiseServicesWithSorting = daywiseServices.map(function ({
		transports,
		activities,
		extras,
		date,
		date_local,
	}) {
		const sortedData: Array<
			(
				| {
						type: "transport"
						transport: Omit<(typeof transports)[0], "activities">
						activity?: undefined
				  }
				| {
						type: "activity"
						transport?: undefined
						activity: (typeof activities)[0]
				  }
			) & {
				start_time: Date | undefined
				sort_order?: number
			}
		> = []
		transports.forEach(({ activities, ...t }) => {
			sortedData.push({
				type: "transport",
				transport: t,
				sort_order: t.sort_order,
				start_time: t.transport_service.start_time
					? parseDate(t.transport_service.start_time, "HH:mm:ss")
					: undefined,
			})
			activities.forEach((a) => {
				sortedData.push({
					type: "activity",
					activity: a,
					sort_order: a.sort_order,
					start_time: a.slot ? parseDate(a.slot, "HH:mm") : undefined,
				})
			})
		})
		activities.forEach((a) => {
			sortedData.push({
				type: "activity",
				activity: a,
				sort_order: a.sort_order,
				start_time: a.slot ? parseDate(a.slot, "HH:mm") : undefined,
			})
		})

		if (sortedData[0]?.sort_order) {
			sortedData.sort((a, b) =>
				a.sort_order && b.sort_order ? a.sort_order - b.sort_order : 0
			)
		} else {
			const canSort = sortedData.every((a) => {
				return Boolean(a.start_time)
			})

			if (canSort) {
				// now sort the data
				sortedData.sort((a, b) => {
					if (!a.start_time || !b.start_time) {
						return 0
					}
					const aStartTime = a.start_time.valueOf()
					const bStartTime = b.start_time.valueOf()
					return aStartTime > bStartTime ? 1 : aStartTime < bStartTime ? -1 : 0
				})
			}
		}

		return {
			date,
			date_local,
			transportsAndActivities: sortedData,
			extras,
		}
	})
	return {
		daywiseServices: daywiseServicesWithSorting,
		extraServiceWithoutDates,
	}
}

/**
 * Get multiplier for old pricing strategies (overall v1/v2, per-person v1/2)
 */

export function getMultiplierForOverallPerPersonPricing(
	tripQuote: Pick<
		ITripQuote,
		| "currency"
		| "cost_price"
		| "given_quote"
		| "quote"
		| "options"
		| "pricing_strategy"
	>
): number {
	const { given_quote, quote, pricing_strategy } = tripQuote
	if (!given_quote) return 0
	const currencyPairs = given_quote.currency_pairs
	const packageCurrency = given_quote.given_currency
	const convertCurrency = makeCurrencyConverter(currencyPairs || [])
	const flightsCostPriceInCostingCurrency = convertCurrency(
		tripQuote.currency,
		addMoneyFromDifferentCurrencies(
			quote.flights.map(({ calculated_price, currency }) =>
				moneyParseByDecimal(calculated_price || 0, currency)
			)
		)
	)

	const flightsGivenPriceInCostingCurrency = convertCurrency(
		tripQuote.currency,
		addMoneyFromDifferentCurrencies(
			quote.flights.map(({ given_price, currency }) =>
				moneyParseByDecimal(given_price || 0, currency)
			)
		)
	)

	const totalGivenPriceInPackageCurrency = moneyParseByDecimal(
		given_quote.given_price,
		packageCurrency
	)

	const flightsCostPriceInPackageCurrency = convertCurrency(
		packageCurrency,
		flightsCostPriceInCostingCurrency
	)

	let flightsGivenPriceInPackageCurrency = convertCurrency(
		packageCurrency,
		flightsGivenPriceInCostingCurrency
	)

	if (
		pricing_strategy === "overall_v2" ||
		pricing_strategy === "per_person_v2"
	) {
		//Old v2 pricing stratgies can have scaled up flight given price because these pricing stratgies have enabled rounding feature for flights
		//whcih can result to scaled up flight final given price from the one which user manually entered.
		const flightGivenPriceInGivenCurrencyComponent =
			given_quote?.given_prices_per_component?.find((c) => c.id === "flights")
				?.price || 0

		if (
			flightGivenPriceInGivenCurrencyComponent &&
			Number(flightGivenPriceInGivenCurrencyComponent || 0) > 0
		) {
			flightsGivenPriceInPackageCurrency = moneyParseByDecimal(
				Number(flightGivenPriceInGivenCurrencyComponent || 0),
				packageCurrency
			)
		}
	}

	const totalCostPriceInPackageCurrency = convertCurrency(
		packageCurrency,
		moneyParseByDecimal(tripQuote.cost_price, tripQuote.currency)
	)

	const ratio = getMoneyRatio(
		subtractMoney(
			totalGivenPriceInPackageCurrency,
			flightsGivenPriceInPackageCurrency
		),
		subtractMoney(
			totalCostPriceInPackageCurrency,
			flightsCostPriceInPackageCurrency
		),
		0
	)

	return ratio
}

/**
 * Get component-specific multiplier for per-component pricing strategies
 * For "per_component_v2" and "per_component_per_person_v2" strategies only
 */
export function getMultiplierForPerComponentPricing(
	tripQuote: ITripQuote,
	componentKey: TComponentKey
): number {
	if (componentKey === "flights") {
		return getMultiplierForPerComponentStrategiesForFlights(tripQuote)
	}

	const { cost_prices_per_component, given_quote } = tripQuote

	const costingCurrency = tripQuote.given_quote?.currency || tripQuote.currency
	const givenCurrency =
		tripQuote.given_quote?.given_currency || tripQuote.currency

	const currencyPairs = given_quote?.currency_pairs

	const convertCurrency = makeCurrencyConverter(currencyPairs || [])

	if (!cost_prices_per_component || !given_quote?.given_prices_per_component) {
		return 1
	}

	const componentCostPrice =
		cost_prices_per_component.find((comp) => comp.id === componentKey)?.price ||
		0

	let componentCostPriceMoneyInGivenCurrency = moneyParseByDecimal(
		Number(componentCostPrice || 0),
		costingCurrency
	)

	if (costingCurrency !== givenCurrency) {
		componentCostPriceMoneyInGivenCurrency = convertCurrency(
			givenCurrency,
			componentCostPriceMoneyInGivenCurrency
		)
	}

	const componentGivenPrice =
		given_quote.given_prices_per_component.find(
			(comp) => comp.id === componentKey
		)?.price || 0

	const componentGivenPriceMoneyInGivenCurrency = moneyParseByDecimal(
		Number(componentGivenPrice || 0),
		givenCurrency
	)

	return getMoneyRatio(
		componentGivenPriceMoneyInGivenCurrency,
		componentCostPriceMoneyInGivenCurrency,
		1
	)
}

/**
 * Get component-specific multiplier for per-component pricing strategies for flights only
 * For "per_component_v2" "per_component_per_person_v2" strategies only
 */
export function getMultiplierForPerComponentStrategiesForFlights(
	tripQuote: ITripQuote
): number {
	const { given_quote, quote } = tripQuote

	const { flights } = quote

	const costingCurrency = tripQuote.given_quote?.currency || tripQuote.currency
	const givenCurrency =
		tripQuote.given_quote?.given_currency || tripQuote.currency

	const currencyPairs = given_quote?.currency_pairs

	const convertCurrency = makeCurrencyConverter(currencyPairs || [])

	if (!given_quote?.given_prices_per_component) {
		return 1
	}

	const flightsGivenPriceInCostingCurrency = convertCurrency(
		costingCurrency,
		addMoneyFromDifferentCurrencies(
			flights.map(({ given_price, currency }) =>
				moneyParseByDecimal(Number(given_price || 0), currency)
			)
		)
	)

	let flightsGivenPricesInGivenCurrency = flightsGivenPriceInCostingCurrency

	if (costingCurrency !== givenCurrency) {
		flightsGivenPricesInGivenCurrency = convertCurrency(
			givenCurrency,
			flightsGivenPriceInCostingCurrency
		)
	}

	const flightsComponentGivenPriceinGivenCurrency =
		given_quote.given_prices_per_component.find((comp) => comp.id === "flights")
			?.price || 0

	const flightsComponentGivenPriceMoneyInGivenCurrency = moneyParseByDecimal(
		Number(flightsComponentGivenPriceinGivenCurrency || 0),
		givenCurrency
	)

	return getMoneyRatio(
		flightsComponentGivenPriceMoneyInGivenCurrency,
		flightsGivenPricesInGivenCurrency,
		1
	)
}

export function orderDaywiseServicesByDate<T extends { dates?: Date[] }>(
	daywiseServices: T[]
): T[] {
	if (!(daywiseServices || []).length) return daywiseServices

	const orderedServices = [...daywiseServices]

	const multiDateIndices = orderedServices.reduce<number[]>(
		(indices, service, index) => {
			if (service.dates && (service.dates || []).length > 1) {
				indices.push(index)
			}
			return indices
		},
		[]
	)

	let startIdx = 0
	for (let i = 0; i <= multiDateIndices.length; i++) {
		const endIdx =
			i < multiDateIndices.length ? multiDateIndices[i] : orderedServices.length

		if (endIdx > startIdx) {
			const segment = orderedServices.slice(startIdx, endIdx)
			segment.sort((a, b) => {
				if (!(a.dates || []).length || !(b.dates || []).length) return 0

				return (
					(a.dates as Date[])[0].getTime() - (b.dates as Date[])[0].getTime()
				)
			})

			for (let j = startIdx; j < endIdx; j++) {
				orderedServices[j] = segment[j - startIdx]
			}
		}

		if (i < multiDateIndices.length) {
			startIdx = multiDateIndices[i] + 1
		}
	}

	return orderedServices
}

//Temp util for preventing quote creation during migration process for new pricing strategies

export function shouldAllowQuoteActions(
	pricingStrategy: ITripQuote["pricing_strategy"] | undefined
) {
	if (pricingStrategy) {
		return true
	}

	return false
}

export function tempDisableQuoteActionsMessageAlert() {
	return alert(
		"Data migration is in progress. This button will be enabled soon. Thank you for your patience."
	)
}

//Given Quote Prices Payload Preparations Helper Functions for Old/New Pricing Stratgies for NewQuoteForm, GiveQuoteForm

type TQuoteFormTaxes = Required<Pick<IGivenQuote, "taxes">>["taxes"]

/**
 * Dispatcher: global tax applied_on by strategy
 * @returns The global tax applied_on
 */

export function prepareGlobalTaxAppliedOnPayloadDispatcher(
	pricingStrategy: TGivenQuotePricingStrategy,
	option1Taxes: TQuoteFormTaxes,
	taxPerComponentAllocation?: TQuoteFormTaxes["per_component_allocation"]
) {
	if (
		pricingStrategy === "per_component_v2" ||
		pricingStrategy === "per_component_per_person_v2"
	) {
		return getUniformGlobalTaxAppliedOnAcrossComponentsV2(
			taxPerComponentAllocation
		)
	}

	return getGlobalTaxAppliedOnForV1(option1Taxes)
}

/**
 * For V2 Pricing Strategies: Tax Applied on Payload Preparation function for V2 pricing strategies: derive uniform global applied_on across components (excluding flights)
 * Determines if all components expects (flights are excluded) have the same tax applied_on value.
 * Returns the common applied_on value if all components match, otherwise returns undefined.
 * @returns The common TTaxAppliedOn value if all components match, undefined otherwise
 *
 */
function getUniformGlobalTaxAppliedOnAcrossComponentsV2(
	perComponentAllocation?: TQuoteFormTaxes["per_component_allocation"]
): TTaxAppliedOn | undefined {
	if (!perComponentAllocation) return undefined

	const appliedOnKeys = [
		"hotels_applied_on",
		"cabs_applied_on",
		"activities_applied_on",
		"extras_applied_on",
		"visas_applied_on",
	] as const

	const appliedOnValues: TTaxAppliedOn[] = []

	appliedOnKeys.forEach((key) => {
		const value = perComponentAllocation[key]
		if (value && (value === "cost_plus_markup" || value === "only_markup")) {
			appliedOnValues.push(value)
		}
	})

	if (appliedOnValues.length === 0) return undefined

	const firstValue = appliedOnValues[0]
	const allSame = appliedOnValues.every((value) => value === firstValue)

	return allSame ? firstValue : undefined
}

/**
 * For V1/V2 Old Pricing Strategies: global applied-on is simply option1's total_applied_on
 * @returns The global tax applied_on
 */
function getGlobalTaxAppliedOnForV1(option1Taxes: TQuoteFormTaxes) {
	return option1Taxes?.total_applied_on || "cost_plus_markup"
}

/**
 * Dispatcher: per-component tax allocation by strategy
 * @returns The per-component tax allocation
 */

export function preparePerComponentTaxAllocationPayloadDispatcher(
	pricingStrategy: TGivenQuotePricingStrategy,
	option1Taxes: TQuoteFormTaxes,
	currentOptionTaxes: TQuoteFormTaxes,
	costPricesPerComponent?: TPricePerTravelComponentAllocation[]
) {
	if (
		pricingStrategy === "per_component_v2" ||
		pricingStrategy === "per_component_per_person_v2"
	) {
		return preparePerComponentTaxAllocationPayloadV2(
			option1Taxes,
			currentOptionTaxes,
			costPricesPerComponent
		)
	}

	// V1 and other old strategies
	return preparePerComponentTaxAllocationPayloadV1(option1Taxes)
}

/**
 * For V1/V2 Old Pricing Strategies: only flights fields included; other components are omitted
 */
function preparePerComponentTaxAllocationPayloadV1(
	option1Taxes: TQuoteFormTaxes
): TQuoteFormTaxes["per_component_allocation"] {
	const allocation: TQuoteFormTaxes["per_component_allocation"] = {
		// Only flights keys present for V1 strategies as there is no per-component allocation for other components feature given to user for old v1 pricing strategies
		flights_applied_on:
			option1Taxes?.per_component_allocation?.flights_applied_on ||
			"cost_plus_markup",
		flights_percentage:
			option1Taxes?.per_component_allocation?.flights_percentage || 0,
		flights: option1Taxes?.per_component_allocation?.flights || 0,
	} as never

	return allocation
}

/**
 *  For V2 Pricing Strategies: Prepares per_component_allocation for taxes with option synchronization logic for v2 pricing strategies.
 * - Global settings (editing_total, total_percentage) are taken from option1Taxes
 * - Most components (cabs, activities, extras, flights) sync tax values from option1Taxes
 * - Hotels component syncs percentage from option1 but keeps its own tax total (due to different hotel costs and markup applied as individual options can have thieir own different markups value).
 * - Sets tax total and percentage to 0 for components with 0 cost price
 *
 */
function preparePerComponentTaxAllocationPayloadV2(
	option1Taxes: TQuoteFormTaxes,
	currentOptionTaxes: TQuoteFormTaxes,
	costPricesPerComponent?: TPricePerTravelComponentAllocation[]
): TQuoteFormTaxes["per_component_allocation"] {
	const globalTaxAppliedOn = option1Taxes.total_applied_on

	const percentageKeys = {
		hotels: "hotels_percentage",
		cabs: "cabs_percentage",
		activities: "activities_percentage",
		extras: "extras_percentage",
		flights: "flights_percentage",
		visas: "visas_percentage",
	} as const

	const totalKeys = {
		hotels: "hotels",
		cabs: "cabs",
		activities: "activities",
		extras: "extras",
		flights: "flights",
		visas: "visas",
	} as const

	const appliedOnKeys = {
		hotels: "hotels_applied_on",
		cabs: "cabs_applied_on",
		activities: "activities_applied_on",
		extras: "extras_applied_on",
		flights: "flights_applied_on",
		visas: "visas_applied_on",
	} as const

	const allocation = { ...currentOptionTaxes.per_component_allocation }

	const costPriceMap = new Map<string, number>()
	if (costPricesPerComponent) {
		costPricesPerComponent.forEach((component) => {
			costPriceMap.set(component.id, component.price)
		})
	}

	const componentsToUpdate = Object.keys(percentageKeys) as TComponentKey[]

	// Sync component values from option1 (excluding hotels which can have different totals)
	const componentToSyncFromOption1: TComponentKey[] = [
		"cabs",
		"activities",
		"extras",
		"flights",
		"visas",
	]

	componentsToUpdate.forEach((componentKey) => {
		const percentageKey = percentageKeys[componentKey]
		const totalKey = totalKeys[componentKey]
		const appliedOnKey = appliedOnKeys[componentKey]

		if (componentToSyncFromOption1.includes(componentKey)) {
			// For non-hotel components: sync both percentage and total from option1
			if (allocation && option1Taxes.per_component_allocation) {
				if (percentageKey in allocation) {
					allocation[percentageKey] =
						option1Taxes.per_component_allocation[percentageKey] || 0
				}
				if (totalKey in allocation) {
					allocation[totalKey] =
						option1Taxes.per_component_allocation[totalKey] || 0
				}
				if (appliedOnKey in allocation) {
					allocation[appliedOnKey] =
						option1Taxes.per_component_allocation[appliedOnKey] ||
						"cost_plus_markup"
				}
			}
		} else if (componentKey === "hotels") {
			// For hotels: sync percentage and applied_on from option1, but keep current option's total
			if (allocation && option1Taxes.per_component_allocation) {
				if (percentageKey in allocation) {
					allocation[percentageKey] =
						option1Taxes.per_component_allocation[percentageKey] || 0
				}
				if (appliedOnKey in allocation) {
					allocation[appliedOnKey] =
						option1Taxes.per_component_allocation[appliedOnKey] ||
						"cost_plus_markup"
				}
			}
		}
	})

	// Handle percentage updates when editing_total is 1 and total_percentage is available (use option1 settings) for all components expect flights
	if (
		Number(option1Taxes.editing_total) === 1 &&
		option1Taxes.total_percentage !== undefined
	) {
		componentsToUpdate
			.filter((c) => c !== "flights")
			.forEach((componentKey) => {
				const percentageKey = percentageKeys[componentKey]
				if (allocation && percentageKey in allocation) {
					allocation[percentageKey] = option1Taxes.total_percentage
				}
			})
	}

	// Handling applied_on updates when globalTaxAppliedOn is available for all components expect flights
	if (globalTaxAppliedOn) {
		componentsToUpdate
			.filter((c) => c !== "flights")
			.forEach((componentKey) => {
				const appliedOnKey = appliedOnKeys[componentKey]
				if (allocation && appliedOnKey in allocation) {
					allocation[appliedOnKey] = globalTaxAppliedOn
				}
			})
	}

	// Set tax total and percentage to 0 for components with 0 cost price
	if (costPricesPerComponent) {
		;(Object.keys(totalKeys) as TComponentKey[]).forEach((componentKey) => {
			const costPrice = costPriceMap.get(componentKey) || 0
			if (costPrice === 0) {
				const totalKey = totalKeys[componentKey]
				const percentageKey = percentageKeys[componentKey]

				if (allocation && totalKey in allocation) {
					allocation[totalKey] = 0
				}
				if (allocation && percentageKey in allocation) {
					allocation[percentageKey] = 0
				}
			}
		})
	}

	return allocation
}

/**
 * Dispatcher: given_prices_per_component payload by strategy
 * - V2 strategies: return current option's given_prices_per_component as-is
 * - V1/V2-old strategies: include only flights
 */

export function prepareGivenPricesPerComponentPayloadDispatcher(
	pricingStrategy: ITripQuote["pricing_strategy"],
	currentOptionGivenPricesPerComponent: IGivenQuote["given_prices_per_component"]
) {
	if (
		pricingStrategy === "per_component_v2" ||
		pricingStrategy === "per_component_per_person_v2"
	) {
		return currentOptionGivenPricesPerComponent
	}

	return prepareGivenPricesPerComponentPayloadV1(
		currentOptionGivenPricesPerComponent
	)
}

/**
 * V1/V2-old strategies: only flights included in given_prices_per_component
 */
function prepareGivenPricesPerComponentPayloadV1(
	currentOptionGivenPricesPerComponent: IGivenQuote["given_prices_per_component"]
) {
	const flightsOnly = (currentOptionGivenPricesPerComponent || []).filter(
		(component) => component.id === "flights"
	)
	return flightsOnly.length ? flightsOnly : undefined
}

/**
 * Dispatcher: unrounded_given_prices.per_component_allocation payload by strategy
 * - V2 strategies: return current option's unrounded_given_prices.per_component_allocation as-is
 * - V1/V2-old strategies: include only flights
 */

export function prepareUnroundedGivenPricesPerComponentPayloadDispatcher(
	pricingStrategy: ITripQuote["pricing_strategy"],
	currentOptionUnroundedGivenPrices: TUnroundedGivenPrice
) {
	const currentOptionUnroundedGivenPricesPerComponent =
		currentOptionUnroundedGivenPrices?.per_component_allocation

	if (
		pricingStrategy === "per_component_v2" ||
		pricingStrategy === "per_component_per_person_v2"
	) {
		return currentOptionUnroundedGivenPricesPerComponent
	}

	return prepareUnroundedGivenPricesPerComponentPayloadV1(
		currentOptionUnroundedGivenPrices
	)
}

/**
 * V1/V2-old strategies: only flights included in unrounded_given_prices.per_component_allocation
 */
function prepareUnroundedGivenPricesPerComponentPayloadV1(
	currentOptionUnroundedGivenPrices: TUnroundedGivenPrice
) {
	const currentOptionUnroundedGivenPricesPerComponent =
		currentOptionUnroundedGivenPrices?.per_component_allocation

	const perComponentAllocationWithFlightOnly =
		currentOptionUnroundedGivenPricesPerComponent
			? ({
					flights: currentOptionUnroundedGivenPricesPerComponent?.flights || 0,
				} as never)
			: undefined

	return perComponentAllocationWithFlightOnly
}

/**
 * Dispatcher: Calls the correct utility for margins based on pricing_strategy.
 */

export function prepareMarginsPayloadForOptionDispatcher(
	currentOptionMargins: IGivenQuote["margins"],
	option1Margins: IGivenQuote["margins"],
	pricingStrategy: TGivenQuotePricingStrategy,
	currentOptionPerComponentCostPriceComponentsTotal: number,
	enableSyncRoundingAmongOptions?: 0 | 1,
	currentOptionCostPricesPerComponent?: Array<TPricePerTravelComponentAllocation>,
	currentOptionCostPricesPerPerson?: Array<TPricePerPersonAllocation>,
	option1CostPricesPerComponent?: Array<TPricePerTravelComponentAllocation>
) {
	// For v2 strategies
	if (
		pricingStrategy === "per_component_v2" ||
		pricingStrategy === "per_component_per_person_v2"
	) {
		return prepareV2StrategyMarginsPayloadForOption(
			currentOptionMargins,
			option1Margins,
			currentOptionPerComponentCostPriceComponentsTotal,
			currentOptionCostPricesPerComponent,
			currentOptionCostPricesPerPerson,
			option1CostPricesPerComponent
		)
	}

	return prepareV1StrategyMarginsPayloadForOption(
		pricingStrategy,
		currentOptionMargins,
		option1Margins,
		enableSyncRoundingAmongOptions,
		currentOptionCostPricesPerPerson,
		option1CostPricesPerComponent
	)
}

/**
 * For old strategies ("overall", "per_person"):
 * It returns the current option's margins as-is because old pricing stratgies do not support sync markup feature among options and
 * to maintain backward compatibility for old quotes we are not syncing markups among options for old pricing stratgies
 * as we are currently doing for new pricing stratgies but we are doing rounding sync among options for new versions of old pricing starategies (Overall V2, Per Person V2).
 */
function prepareV1StrategyMarginsPayloadForOption(
	pricingStrategy: ITripQuote["pricing_strategy"],
	currentOptionMargins: IGivenQuote["margins"],
	option1Margins: IGivenQuote["margins"],
	enableSyncRoundingAmongOptions?: 0 | 1,
	currentOptionCostPricesPerPerson?: Array<TPricePerPersonAllocation>,
	option1CostPricesPerComponent?: Array<TPricePerTravelComponentAllocation>
) {
	if (!currentOptionMargins) return undefined

	const hasCost = (
		componentKey: string,
		list?: Array<TPricePerTravelComponentAllocation>
	) =>
		(list || []).some((c) => c.id === componentKey && Number(c.price || 0) > 0)

	const perComponentAllocation = {
		// Only flights keys will be sent to api for old pricing strategies as there is no per-component allocation for other components feature given to user for old v1 pricing strategies
		// Also as currenctly flight tax, cost is global/same among options so calculated markup will also be same, so we can safely rely on 1st option option1Margins data.
		flights: hasCost("flights", option1CostPricesPerComponent)
			? option1Margins?.per_component_allocation?.flights || 0
			: 0,
		flights_percentage: hasCost("flights", option1CostPricesPerComponent)
			? option1Margins?.per_component_allocation?.flights_percentage || 0
			: 0,
	} as never

	return {
		...currentOptionMargins,
		per_component_allocation: perComponentAllocation,
		per_person_allocation: prepareNormalizePerPersonMarginAllocation(
			currentOptionMargins,
			currentOptionCostPricesPerPerson
		),
		rounding_point: enableSyncRoundingAmongOptions
			? option1Margins?.rounding_point
			: currentOptionMargins?.rounding_point,
		editing_total: prepareNormalizeMarkupEditingTotalFlag(
			pricingStrategy,
			currentOptionMargins
		),
	}
}

/**
 * For v2 strategies ("per_component", "per_component_per_person"):
 * - Sync all component markups (except hotels) from option1Margins.
 * - Only hotels markup is taken from the current option.
 */
function prepareV2StrategyMarginsPayloadForOption(
	currentOptionMargins: IGivenQuote["margins"],
	option1Margins: IGivenQuote["margins"],
	currentOptionPerComponentCostPriceComponentsTotal: number,
	currentOptionCostPricesPerComponent?: Array<TPricePerTravelComponentAllocation>,
	currentOptionCostPricesPerPerson?: Array<TPricePerPersonAllocation>,
	option1CostPricesPerComponent?: Array<TPricePerTravelComponentAllocation>
) {
	const totalMarkup = currentOptionMargins?.total || 0

	// For v2 strategies

	const totalMarkupPercentage =
		currentOptionPerComponentCostPriceComponentsTotal > 0
			? normalizeNumber(
					(totalMarkup / currentOptionPerComponentCostPriceComponentsTotal) *
						100,
					6
				)
			: 0

	const hasCost = (
		componentKey: string,
		list?: Array<TPricePerTravelComponentAllocation>
	) =>
		(list || []).some((c) => c.id === componentKey && Number(c.price || 0) > 0)

	return {
		...currentOptionMargins,
		total: totalMarkup,
		total_percentage: totalMarkupPercentage,

		//expect hotels, we are syncining all component markups of all options based on option 1,
		//refer to comment on code line 529 for more details
		per_component_allocation: {
			//Note - We are normalizing per component allocaiton of margins based on their cost price, because during quote creation there can be case when user entered
			//a markup for a component and it persists in the form state but user made changes to the cost prices which result that component can either be not avaialble any more
			//or it can have 0 cost price but inside the form the user entered margin for the allocation still persits and to avoid race condtions it is better to clean up un-necesaary state
			//during payload preparation instead of trying to sync the state and cleanups during quote creation for the form.

			hotels: hasCost("hotels", currentOptionCostPricesPerComponent)
				? currentOptionMargins?.per_component_allocation?.hotels || 0
				: 0,
			hotels_percentage: hasCost("hotels", currentOptionCostPricesPerComponent)
				? currentOptionMargins?.per_component_allocation?.hotels_percentage || 0
				: 0,
			cabs: hasCost("cabs", option1CostPricesPerComponent)
				? option1Margins?.per_component_allocation?.cabs || 0
				: 0,
			cabs_percentage: hasCost("cabs", option1CostPricesPerComponent)
				? option1Margins?.per_component_allocation?.cabs_percentage || 0
				: 0,
			activities: hasCost("activities", option1CostPricesPerComponent)
				? option1Margins?.per_component_allocation?.activities || 0
				: 0,
			activities_percentage: hasCost(
				"activities",
				option1CostPricesPerComponent
			)
				? option1Margins?.per_component_allocation?.activities_percentage || 0
				: 0,
			extras: hasCost("extras", option1CostPricesPerComponent)
				? option1Margins?.per_component_allocation?.extras || 0
				: 0,
			extras_percentage: hasCost("extras", option1CostPricesPerComponent)
				? option1Margins?.per_component_allocation?.extras_percentage || 0
				: 0,
			flights: hasCost("flights", option1CostPricesPerComponent)
				? option1Margins?.per_component_allocation?.flights || 0
				: 0,
			flights_percentage: hasCost("flights", option1CostPricesPerComponent)
				? option1Margins?.per_component_allocation?.flights_percentage || 0
				: 0,
			visas: hasCost("visas", option1CostPricesPerComponent)
				? option1Margins?.per_component_allocation?.visas || 0
				: 0,
			visas_percentage: hasCost("visas", option1CostPricesPerComponent)
				? option1Margins?.per_component_allocation?.visas_percentage || 0
				: 0,
		},
		per_person_allocation: prepareNormalizePerPersonMarginAllocation(
			currentOptionMargins,
			currentOptionCostPricesPerPerson
		),
		rounding_point: option1Margins?.rounding_point,
		editing_total: 0 as TQuoteFormTaxes["editing_total"], //currently both v2 stratigies do not have feature to add markup on total
		use_percentage:
			typeof option1Margins?.use_percentage !== "undefined"
				? option1Margins?.use_percentage
				: 1,
	}
}

/**
 * Dispatcher: Calls the correct utility for taxes based on pricing_strategy.
 */

export function prepareTaxPercentagePayloadForOptionDispatcher(
	pricingStrategy: ITripQuote["pricing_strategy"],
	option1Taxes: TQuoteFormTaxes,
	totalTax: number,
	totalCostPrice: number,
	totalMarkup: number,
	globalTaxAppliedOn?: TQuoteFormTaxes["total_applied_on"]
) {
	if (
		pricingStrategy === "overall_v1" ||
		pricingStrategy === "overall_v2" ||
		pricingStrategy === "per_person_v1" ||
		pricingStrategy === "per_person_v2"
	) {
		return prepareTotalTaxPercentageForV1Strategies(option1Taxes)
	}
	// For v2 strategies
	return prepareTotalTaxPercentageForV2Strategies(
		option1Taxes,
		totalTax,
		totalCostPrice,
		totalMarkup,
		globalTaxAppliedOn
	)
}

/**
 * Prepares the total tax percentage for the taxes object for v1 pricing strategies.
 * As v1 pricing stratgies have support of tax percentage on total cost price, there is no scenerio of individual taxes for per-component therefore, it just returns the existing total_percentage entered by the user in the tax input.
 */
function prepareTotalTaxPercentageForV1Strategies(
	option1Taxes: TQuoteFormTaxes
): number {
	return option1Taxes.total_percentage
}

/**
 * Prepares the total tax percentage for the taxes object.
 * - If editing_total is true (1), returns the existing total_percentage.
 * - If editing_total is false (0), calculates the percentage based on totals and applied_on.
 */
function prepareTotalTaxPercentageForV2Strategies(
	option1Taxes: TQuoteFormTaxes,
	totalTax: number,
	totalCostPrice: number,
	totalMarkup: number,
	globalTaxAppliedOn?: TQuoteFormTaxes["total_applied_on"]
): number {
	if (Number(option1Taxes.editing_total) === 1) {
		return option1Taxes.total_percentage
	}
	if (globalTaxAppliedOn === "only_markup") {
		return totalMarkup > 0
			? normalizeNumber((totalTax / totalMarkup) * 100, 6)
			: 0
	}
	// Default: cost_plus_markup
	const base = totalCostPrice + totalMarkup
	return base > 0 ? normalizeNumber((totalTax / base) * 100, 6) : 0
}

/**
 * Dispatcher: Calls the correct utility for taxes editing_total based on pricing_strategy.
 */

export function prepareTaxEditingTotalPayloadForOptionDispatcher(
	pricingStrategy: ITripQuote["pricing_strategy"],
	option1Taxes: TQuoteFormTaxes
) {
	if (
		pricingStrategy === "overall_v1" ||
		pricingStrategy === "overall_v2" ||
		pricingStrategy === "per_person_v1" ||
		pricingStrategy === "per_person_v2"
	) {
		return 1 //for old strategies, editing_total is always 1 as there is no support for individual tax editing as we have for advance v2 pricing strategies
	}
	// For v2 strategies
	return option1Taxes.editing_total
}

/**
 * We are normalizing per person allocaiton of margins based on their cost price, because during quote creation there can be case when user entered
 * a markup for allocation id and it persists in the form state but user made changes to the cost prices which result that allocation id can either be not avaialble any more
 * or it can have 0 cost price but inside the form the user entered margin for the allocation still persits and to avoid race condtions it is better to clean up un-necesaary state
 * during payload preparation instead of trying to sync the state and cleanups during quote creation for the form.
 */

function prepareNormalizePerPersonMarginAllocation(
	currentOptionMargins: IGivenQuote["margins"],
	currentOptionCostPricesPerPerson?: Array<TPricePerPersonAllocation>
) {
	if (!currentOptionMargins) return undefined

	let normalizedPerPersonAllocation = currentOptionMargins.per_person_allocation

	if (normalizedPerPersonAllocation && currentOptionCostPricesPerPerson) {
		const validAllocationCostPriceIds = new Map(
			currentOptionCostPricesPerPerson.map(({ id, price }) => [
				id,
				Number(price || 0) > 0,
			])
		)

		normalizedPerPersonAllocation = { ...normalizedPerPersonAllocation }

		Object.keys(normalizedPerPersonAllocation).forEach((key) => {
			const baseId = key.endsWith("_percentage")
				? key.slice(0, -"_percentage".length)
				: key

			const isValid = validAllocationCostPriceIds.get(baseId)

			if (!isValid && normalizedPerPersonAllocation) {
				normalizedPerPersonAllocation[key] = 0
			}
		})
	}

	return normalizedPerPersonAllocation
}

/**
This util is created for purpose to handles 2 issues:

1. When we switch from overall to per person and change markup from edition total to editing individual then we went back on overall then UI correctly showing that you are entering margin on total but in the state “editing_total” is still “0” instead of “1”.
As markup can be entered only on total in overall pricing strategy so we are explicitly settng editing_total as 1 if pricing_strategy is overall.

2. When user is entering markup on per person in "Amount" mode, then he toggles to "Percentage", as currently there is no support to add markup to individual person in "Percentage", the component
handles this perfectly to show allow user to enter markup on total, but in the state form still maintains the flag of "editing_total" as "0" instead of "1".

Note - There is reason we are not handling the reset of this flag in these cases at component level during form creation, as it can create un-expected behaviour,
for example - if we take example of 1st issue, in this case user is just changing pricing strategy to "Overall" and user can toggle back to "Per-Person" and we must persist,
his last preference of either entering markup at "per indiviudal" level or on "total".
*/

function prepareNormalizeMarkupEditingTotalFlag(
	pricingStrategy: ITripQuote["pricing_strategy"],
	currentOptionMargins: NonNullable<IGivenQuote["margins"]>
) {
	if (pricingStrategy === "overall_v1" || pricingStrategy === "overall_v2") {
		return 1
	}

	if (
		pricingStrategy === "per_person_v1" ||
		pricingStrategy === "per_person_v2"
	) {
		if (Number(currentOptionMargins.use_percentage)) {
			return 1
		} //As of now there is no option on the quote form to add markup in percentage to individual per person, so In this case markup.editing_total will alawys be 1.
		//TODO-REMOVE - Remove it after introducing support of per-person indivual markup in "Percentage".
	}

	return currentOptionMargins.editing_total
}

/**
 * DEV NOTE: Automatic Pricing Strategy Migration
 *
 * This function determines the final pricing strategy to send to the API based on feature usage.
 * It implements the v1→v2 migration logic.
 *
 * MIGRATION RULES:
 * 1. overall_v1 + flight_features → overall_v2
 * 2. per_person_v1 + flight_features → per_person_v2
 * 3. per_component strategies → unchanged (already v2)
 * 4. afterConfirmedQuote=true → no migration (preserve confirmed quotes)
 */

export function determineFinalPricingStrategy(
	currentStrategy: TGivenQuotePricingStrategy,
	isEditingExistingQuote: boolean,
	migrateToV2?: 0 | 1
): TGivenQuotePricingStrategy {
	// Only migrate v1 strategies when not editing a exisitng quote

	if (isEditingExistingQuote) {
		return currentStrategy
	}

	const shouldMigrate = isTruthy(migrateToV2)

	// Migrate v1 -> v2 if flight features are enabled
	if (shouldMigrate) {
		if (currentStrategy === "overall_v1") {
			return "overall_v2"
		}
		if (currentStrategy === "per_person_v1") {
			return "per_person_v2"
		}
	}

	// Keep original strategy for v2 strategies or when no migration needed
	return currentStrategy
}

/**
 * Centralized Pricing Migration Hook (Helps for Migration from v1 to v2 pricing strategies) - Only Use inside components which deals with updating quotation given quote data like - NewQuoteForm, GiveQuoteForm
 *
 * This uses the shared hook to determine flight features and stores the state
 * in the form for consistent usage during payload preparation. Handles the the sync of migration flag to the form state which later helpful for payload preparation for pricing stratgy
 *
 * FEATURE FLAGS:
 *
 * A) enableFlightRounding (Conditional):
 *    - per_component_v2/per_component_per_person_v2: ALWAYS enabled
 *    - overall_v1/v2 + per_person_v1/v2: Only when creating a new quote
 *    - This prevents breaking confirmed quotes given prices due to enabling flight rounding feature for all 6 pricing strategies
 *
 * B) enableSyncRoundingAmongOptions (Conditional):
 *    - per_component_v2/per_component_per_person_v2: ALWAYS enabled
 *    - overall_v1/v2 + per_person_v1/v2: Only when creating a new quote
 *    - This prevents breaking confirmed quotes given prices due to enabling sync rounding among options feature for all 6 pricing strategies
 *
 * MIGRATION LOGIC:
 * - When enableFlightRounding=false: Original flight values without rounding
 * - When enableFlightRounding=true: Enhanced flight row values with rounding
 * - API strategy automatically upgraded when flight rounding feature and sync rounding among options feature is used
 */

export function usePricingStrategyMigrationSync({
	pricingStrategy,
	editingExistingQuote,
}: {
	pricingStrategy: TGivenQuotePricingStrategy
	editingExistingQuote: boolean
}) {
	const { onChange: setMigrateToV2 } = useFieldValue<0 | 1>("migrate_to_v2")
	const { onChange: setEnableFlightRounding } = useFieldValue<0 | 1>(
		"enable_flight_rounding"
	)
	const { onChange: setEnableSyncRoundingAmongOptions } = useFieldValue<0 | 1>(
		"enable_sync_rounding_among_options"
	)

	// Decide if flight rounding is enabled
	// False only when editing existing quotes with v1 strategies, otherwise always True
	const enableFlightRounding = useMemo(() => {
		if (
			pricingStrategy === "per_component_v2" ||
			pricingStrategy === "per_component_per_person_v2" ||
			pricingStrategy === "overall_v2" ||
			pricingStrategy === "per_person_v2"
		) {
			return true // Always enabled for v2 Strategies
		}
		if (
			pricingStrategy === "overall_v1" ||
			pricingStrategy === "per_person_v1"
		) {
			// - When clicking "Edit Quote" on an older v1 quotation, we get pricingStrategy == v1, and editing=1 ==> We must not upgrade to v2 (i.e. don't round flight prices)
			// - When clicking "Use This" with an older v1 quotation, we get pricingStrategy == v1, and editing=0 ==> We must upgrade to v2 (i.e. round flight prices)
			return !editingExistingQuote
		}
		return false
	}, [pricingStrategy, editingExistingQuote])

	//Decide if rounding will be sync among options

	const enableSyncRoundingAmongOptions = useMemo(() => {
		if (
			pricingStrategy === "per_component_v2" ||
			pricingStrategy === "per_component_per_person_v2" ||
			pricingStrategy === "overall_v2" ||
			pricingStrategy === "per_person_v2"
		) {
			return true // Always enabled for v2 Strategies
		}
		if (
			pricingStrategy === "overall_v1" ||
			pricingStrategy === "per_person_v1"
		) {
			return !editingExistingQuote // Only when not ediitng exisitng old quotes with v1 strategies
		}
		return false
	}, [pricingStrategy, editingExistingQuote])

	// Decide if migration will happen
	const isMigrating =
		enableFlightRounding &&
		enableSyncRoundingAmongOptions &&
		(pricingStrategy === "overall_v1" || pricingStrategy === "per_person_v1")

	// Sync migration state into form
	useEffect(() => {
		const h = setTimeout(() => {
			setMigrateToV2(isMigrating ? 1 : 0)

			setEnableFlightRounding(enableFlightRounding ? 1 : 0)

			setEnableSyncRoundingAmongOptions(enableSyncRoundingAmongOptions ? 1 : 0)
		}, 100)

		return () => {
			clearTimeout(h)
		}
	}, [
		isMigrating,
		enableFlightRounding,
		enableSyncRoundingAmongOptions,
		setEnableFlightRounding,
		setEnableSyncRoundingAmongOptions,
		setMigrateToV2,
	])

	return {
		enableFlightRounding,
		enableSyncRoundingAmongOptions,
		isMigrating,
	}
}

/**
 * Use this utility function to determine if a tax should be displayed based on given quote data, It just mainly returns the tax percentage and a flag which determines, if we should display the tax percentage if all travel component have uniform tax applied.
 * @warning This utility is designed to show tax percentage of given quote for display or read only purpose only. If you're using it on forms which deals with quote tax_percentage, then use it wisely by understanding it's purpose.
 * @returns tax_percentage and shouldDisplay flag and tax included or not
 */
export const getGivenQuoteTaxInfo = (givenQuote: IGivenQuote) => {
	const taxPercentage =
		givenQuote.taxes?.total_percentage || givenQuote.tax_percentage || 0

	if (!givenQuote.taxes) {
		return {
			taxPercentage: taxPercentage,
			shouldDisplay: false,
			taxIncluded: false,
		}
	}

	const { taxes, gst_included } = givenQuote

	const isAppliedOnTotal = isTruthy(taxes.editing_total)

	if (isAppliedOnTotal) {
		return {
			taxPercentage: taxPercentage,
			shouldDisplay: true,
			taxIncluded: gst_included,
		}
	}

	// If tax is NOT applied on total, check if all existing components have uniform tax percentage and applied_on
	const { per_component_allocation } = taxes
	const componentKeys: TComponentKey[] = [
		"hotels",
		"cabs",
		"activities",
		"extras",
		"flights",
	]

	// Filter to only components that actually exist in per_component_allocation
	const existingComponents = componentKeys.filter((component) => {
		const componentPercentageKey =
			`${component}_percentage` as keyof typeof per_component_allocation
		const componentAppliedOnKey =
			`${component}_applied_on` as keyof typeof per_component_allocation

		return (
			per_component_allocation[componentPercentageKey] !== undefined &&
			per_component_allocation[componentAppliedOnKey] !== undefined
		)
	})

	// If no components exist, don't display
	if (existingComponents.length === 0) {
		return {
			taxPercentage: taxPercentage,
			shouldDisplay: false,
			taxIncluded: gst_included,
		}
	}

	const firstExistingComponent = existingComponents[0]
	const referencePercentage =
		per_component_allocation[`${firstExistingComponent}_percentage`]
	const referenceAppliedOn =
		per_component_allocation[`${firstExistingComponent}_applied_on`]

	// Check if all existing components have the same percentage and applied_on
	const isAllComponentsHaveUniformTax = existingComponents.every(
		(component) => {
			const componentPercentageKey =
				`${component}_percentage` as keyof typeof per_component_allocation
			const componentAppliedOnKey =
				`${component}_applied_on` as keyof typeof per_component_allocation

			const componentPercentage =
				per_component_allocation[componentPercentageKey]
			const componentAppliedOn = per_component_allocation[componentAppliedOnKey]

			return (
				componentPercentage === referencePercentage &&
				componentAppliedOn === referenceAppliedOn
			)
		}
	)

	return {
		taxPercentage: taxPercentage,
		shouldDisplay: isAllComponentsHaveUniformTax ? true : false,
		taxIncluded: gst_included,
	}
}

const AllocationServiceTitles: Record<
	TPricePerPersonAllocation["service"],
	string
> = {
	accommodation: "Accommodation",
	flight: "Flight",
}

type TGroupedPricePerPerson = {
	service: TPricePerPersonAllocation["service"]
	title: string
	prices: TPricePerPersonAllocation[]
	subtotal: TMoney
}

// separation of per person prices by service
export function groupPricesPerPersonByService(
	pricesPerPerson: TPricePerPersonAllocation[]
): TGroupedPricePerPerson[] {
	const groupsByService = new Map<
		TPricePerPersonAllocation["service"],
		TPricePerPersonAllocation[]
	>()

	for (const price of pricesPerPerson) {
		const prices = groupsByService.get(price.service) ?? []
		prices.push(price)
		groupsByService.set(price.service, prices)
	}
	const services = [...groupsByService.keys()]
	const showTitles = services.length > 1
	const groupsByServiceArray = [...groupsByService]

	return groupsByServiceArray.map(([service, prices]) => {
		const otherServices = services
			.filter((s) => s !== service)
			.map((s) => AllocationServiceTitles[s])
		const subtotal = prices
			.map((p) =>
				multipleMoneyBy(
					{ amount: Number(p.price), currency: p.currency },
					p.pax_count
				)
			)
			.reduce((acc, lineTotal) => addMoney(acc, lineTotal))

		return {
			service,
			prices,
			subtotal,
			title: !showTitles
				? ""
				: service === "accommodation"
					? `Prices Excluding ${otherServices.join(", ")}`
					: `${AllocationServiceTitles[service] ?? service} Prices`,
		}
	})
}

export function allocatePax({
	quote,
	costingCurrency,
	includeAdultsWithExtraBedInCabs,
	includeChildrenInCabsStartAge,
	includeChildrenInExtras,
	excludeAdultsCountInCabs,
}: {
	quote: IQuote
	costingCurrency: string
	includeAdultsWithExtraBedInCabs?: boolean
	includeChildrenInCabsStartAge: false | number
	includeChildrenInExtras?: boolean
	excludeAdultsCountInCabs: number
}) {
	const {
		no_of_adults,
		children,
		hotels,
		cabs,
		travel_activities,
		hotel_extras,
		transport_extras,
		other_extras,
		flights,
		currency_pairs,
		add_cab_prices_at_once,
		visas,
	} = quote
	const pax: Array<{
		id: number
		service: "accommodation"
		type: "adult" | "child"
		accommodation?: "base" | "extra_bed" | "no_extra_bed" | "complimentary"
		room_sharing_type?: number
		price: TMoney[]
		hotels_price: TMoney[]
		cabs_price: TMoney[]
		travel_activities_price: TMoney[]
		flights_price: TMoney[]
		extras_price: TMoney[]
		hotel_extras_price: TMoney[]
		transport_extras_price: TMoney[]
		visas_price: TMoney[]
		age?: number
	}> = []
	const converter = makeCurrencyConverter(currency_pairs, true)

	function convertMoneyToCostingCurrencyAndGetDecimalAmount(
		money: TMoney | Array<TMoney>
	): number {
		return Number(formatMoneyByDecimal(converter(costingCurrency, money)))
	}

	function addPax(type: "adult" | "child", age?: number) {
		pax.push({
			id: pax.length,
			service: "accommodation",
			type,
			price: [createMoney(0, costingCurrency)],
			age,
			hotels_price: [createMoney(0, costingCurrency)],
			cabs_price: [createMoney(0, costingCurrency)],
			travel_activities_price: [createMoney(0, costingCurrency)],
			flights_price: [createMoney(0, costingCurrency)],
			extras_price: [createMoney(0, costingCurrency)],
			hotel_extras_price: [createMoney(0, costingCurrency)],
			transport_extras_price: [createMoney(0, costingCurrency)],
			visas_price: [createMoney(0, costingCurrency)],
		})
	}

	/**
	 * ---------------------------------------------------------
	 * CREATE ADULT PAX
	 * ---------------------------------------------------------
	 */

	for (let i = 0; i < no_of_adults; i++) {
		addPax("adult")
	}

	/**
	 * ---------------------------------------------------------
	 * CREATE CHILD PAX
	 * ---------------------------------------------------------
	 */

	const sortedChildren = children
		.concat([])
		.sort((a, b) => (a.age > b.age ? -1 : a.age === b.age ? 0 : 1))

	for (const child of sortedChildren) {
		for (let i = 0; i < child.count; i++) {
			addPax("child", child.age)
		}
	}

	/**
	 * ---------------------------------------------------------
	 * ADD PRICE TO PAX
	 * ---------------------------------------------------------
	 */

	function addPriceToPax(
		p: (typeof pax)[0],
		type:
			| "hotels"
			| "cabs"
			| "activities"
			| "flights"
			| "extras"
			| "hotel_extras"
			| "transport_extras"
			| "visas",
		amount: TMoney[] | undefined | null
	) {
		const price = amount || []

		p.price = addMoneyFromDifferentCurrencies(p.price.concat(price))

		switch (type) {
			case "hotels":
				p.hotels_price = addMoneyFromDifferentCurrencies(
					p.hotels_price.concat(price)
				)
				break

			case "cabs":
				p.cabs_price = addMoneyFromDifferentCurrencies(
					p.cabs_price.concat(price)
				)
				break

			case "activities":
				p.travel_activities_price = addMoneyFromDifferentCurrencies(
					p.travel_activities_price.concat(price)
				)
				break

			case "flights":
				p.flights_price = addMoneyFromDifferentCurrencies(
					p.flights_price.concat(price)
				)
				break

			case "extras":
				p.extras_price = addMoneyFromDifferentCurrencies(
					p.extras_price.concat(price)
				)
				break

			case "hotel_extras":
				p.hotel_extras_price = addMoneyFromDifferentCurrencies(
					p.hotel_extras_price.concat(price)
				)
				break

			case "transport_extras":
				p.transport_extras_price = addMoneyFromDifferentCurrencies(
					p.transport_extras_price.concat(price)
				)
				break

			case "visas":
				p.visas_price = addMoneyFromDifferentCurrencies(
					p.visas_price.concat(price)
				)
				break
		}

		return p
	}

	/**
	 * ---------------------------------------------------------
	 * HOTEL ALLOCATION
	 * ---------------------------------------------------------
	 */
	let warnings: Array<string> = []
	collect(hotels)
		.groupBy((h) => h.date)
		.toArray()
		.forEach((hotelsForDate) => {
			try {
				const { warnings: hotelWarnings } = allocatePaxInHotels(
					pax,
					hotelsForDate,
					{
						defaultCurrency: costingCurrency,
						addPriceToPax: (p, price) => addPriceToPax(p, "hotels", price),
					}
				)

				warnings = warnings.concat(hotelWarnings)
			} catch (error) {
				warnings.push(
					error instanceof Error
						? error.message
						: "Unable to allocate hotel prices."
				)
			}
		})
	/**
	 * ---------------------------------------------------------
	 * CAB PAX
	 * ---------------------------------------------------------
	 */

	const includedPaxInCabs = pax
		.filter((p) => {
			if (
				p.type === "child" &&
				(includeChildrenInCabsStartAge === false ||
					(p.age || 0) < includeChildrenInCabsStartAge)
			) {
				return false
			}

			if (
				p.type === "adult" &&
				p.accommodation === "extra_bed" &&
				!includeAdultsWithExtraBedInCabs
			) {
				return false
			}

			return true
		})
		.sort((a, b) => {
			if (a.type === "adult" && b.type === "adult") {
				if (
					a.accommodation === "extra_bed" &&
					b.accommodation !== "extra_bed"
				) {
					return -1
				}

				if (
					a.accommodation !== "extra_bed" &&
					b.accommodation === "extra_bed"
				) {
					return 1
				}
			}

			return 0
		})
		.slice(excludeAdultsCountInCabs)

	/**
	 * ---------------------------------------------------------
	 * CAB PRICE ALLOCATION
	 * ---------------------------------------------------------
	 */

	if (add_cab_prices_at_once) {
		const totalCabsPrice = cabs.reduce<Array<TMoney>>(
			(total, cab) =>
				addMoneyFromDifferentCurrencies(
					total.concat([
						moneyParseByDecimal(cab.given_price || 0, cab.currency),
					])
				),
			[]
		)

		const perPaxPrice = totalCabsPrice.map((price) =>
			divideMoneyBy(price, includedPaxInCabs.length || 1, "ceil")
		)

		includedPaxInCabs.forEach((p) => {
			addPriceToPax(p, "cabs", perPaxPrice)
		})
	} else {
		collect(cabs)
			.groupBy((cab) => cab.date)
			.toArray()
			.forEach((cabsForDate) => {
				const totalCabsPriceForThisDay = cabsForDate.reduce<Array<TMoney>>(
					(total, cab) =>
						addMoneyFromDifferentCurrencies(
							total.concat([
								moneyParseByDecimal(cab.given_price || 0, cab.currency),
							])
						),
					[]
				)

				const perPaxPrice = totalCabsPriceForThisDay.map((money) =>
					divideMoneyBy(money, includedPaxInCabs.length || 1, "ceil")
				)

				includedPaxInCabs.forEach((p) => {
					addPriceToPax(p, "cabs", perPaxPrice)
				})
			})
	}

	/**
	 * ---------------------------------------------------------
	 * TRAVEL ACTIVITIES
	 * ---------------------------------------------------------
	 */

	collect(travel_activities)
		.groupBy((d) => d.date)
		.toArray()
		.forEach((travelActivities) => {
			travelActivities.forEach((travelActivity) => {
				const allocatedPaxId: {
					[
						key: number
					]: (typeof travelActivity.ticket_tourist_configurations)[number]
				} = {}

				travelActivity.ticket_tourist_configurations.forEach((ticket) => {
					let assignedTo: (typeof pax)[0] | undefined

					const { configuration } = ticket
					const { age_start, age_end } = configuration

					let perPaxPriceForGroup = 0
					let availabePaxForGroup: typeof pax = []

					switch (ticket.configuration.tourist_type) {
						/**
						 * ---------------------------------
						 * ADULT TICKET
						 * ---------------------------------
						 */

						case "adult": {
							const totalQuantity = ticket.quantity || 0
							const complementary = ticket.complementary || 0
							const chargeableQuantity = totalQuantity - complementary

							for (let i = 0; i < totalQuantity; i++) {
								assignedTo = pax.find(
									(p) => !allocatedPaxId[p.id] && p.type === "adult"
								)

								if (assignedTo) {
									if (i < chargeableQuantity) {
										addPriceToPax(assignedTo, "activities", [
											moneyParseByDecimal(
												ticket.per_quantity_given_price || 0,
												ticket.currency
											),
										])
									}

									allocatedPaxId[assignedTo.id] = ticket
								}
							}

							break
						}

						/**
						 * ---------------------------------
						 * CHILD TICKET
						 * ---------------------------------
						 */

						case "child": {
							const totalQuantity = ticket.quantity || 0
							const complementary = ticket.complementary || 0
							const chargeableQuantity = totalQuantity - complementary

							for (let i = 0; i < totalQuantity; i++) {
								if (
									age_start !== undefined &&
									age_start !== null &&
									age_end !== undefined &&
									age_end !== null
								) {
									assignedTo = pax.find(
										(p) =>
											!allocatedPaxId[p.id] &&
											p.type === "child" &&
											p.age !== undefined &&
											p.age >= age_start &&
											p.age <= age_end
									)

									if (assignedTo) {
										if (i < chargeableQuantity) {
											addPriceToPax(assignedTo, "activities", [
												moneyParseByDecimal(
													ticket.per_quantity_given_price || 0,
													ticket.currency
												),
											])
										}

										allocatedPaxId[assignedTo.id] = ticket
									}
								}
							}

							break
						}

						/**
						 * ---------------------------------
						 * GROUP TICKET
						 * ---------------------------------
						 */

						case "group": {
							availabePaxForGroup = pax.filter((p) => {
								if (allocatedPaxId[p.id]) {
									return false
								}

								if (!p.age) {
									return true
								}

								if (!age_start && !age_end) {
									return !travelActivity.activity.complimentary_till_age
										? true
										: p.age > travelActivity.activity.complimentary_till_age
								}

								return (
									p.age >=
										(age_start ||
											travelActivity.activity.complimentary_till_age ||
											0) && p.age <= (age_end || Infinity)
								)
							})

							perPaxPriceForGroup = Math.ceil(
								(ticket.given_price || 0) /
									Math.min(
										availabePaxForGroup.length || 1,
										ticket.configuration.quantity * ticket.quantity
									)
							)

							for (
								let i = 0;
								i < ticket.quantity * ticket.configuration.quantity;
								i++
							) {
								assignedTo = availabePaxForGroup.find(
									(p) => !allocatedPaxId[p.id]
								)

								if (assignedTo) {
									addPriceToPax(assignedTo, "activities", [
										moneyParseByDecimal(perPaxPriceForGroup, ticket.currency),
									])

									allocatedPaxId[assignedTo.id] = ticket
								}
							}

							break
						}
					}
				})

				/**
				 * -----------------------------------------
				 * FIND UNALLOCATED TICKETS
				 * -----------------------------------------
				 */

				const allocatedTickets = Object.values(allocatedPaxId)

				let unallocatedTickets = travelActivity.ticket_tourist_configurations
					.map((ticket) => {
						let allocatedQuantity = allocatedTickets.filter(
							(t) => t === ticket
						).length

						const perTicketQuantity = ticket.configuration.quantity
						const availableQuantity = ticket.quantity * perTicketQuantity

						const tickets = allocatedQuantity
							? Math.ceil(
									Math.max(allocatedQuantity, perTicketQuantity) /
										perTicketQuantity
								)
							: 0

						allocatedQuantity = tickets * perTicketQuantity

						return {
							ticket,
							unallocatedQuantity: Math.ceil(
								(availableQuantity - allocatedQuantity) / perTicketQuantity
							),
						}
					})
					.filter((ticket) => ticket.unallocatedQuantity > 0)

				/**
				 * -----------------------------------------
				 * FALLBACK:
				 * UNALLOCATED ADULT TICKETS -> CHILDREN
				 * -----------------------------------------
				 */

				if (unallocatedTickets.length) {
					const unallocatedChildren = pax.filter(
						(p) => !allocatedPaxId[p.id] && p.type === "child"
					)

					unallocatedChildren.sort((a, b) =>
						!a.age || !b.age ? 0 : a.age > b.age ? -1 : 1
					)

					const unallocatedTicketsForAdults = unallocatedTickets.filter(
						({ ticket }) => ticket.configuration.tourist_type === "adult"
					)

					unallocatedChildren.forEach((assignedTo) => {
						const ticket = unallocatedTicketsForAdults.find(
							({ unallocatedQuantity }) => unallocatedQuantity > 0
						)

						if (ticket) {
							addPriceToPax(assignedTo, "activities", [
								moneyParseByDecimal(
									ticket.ticket.per_quantity_given_price || 0,
									ticket.ticket.currency
								),
							])

							allocatedPaxId[assignedTo.id] = ticket.ticket

							ticket.unallocatedQuantity -= 1
						}
					})

					unallocatedTickets = unallocatedTickets.filter(
						({ unallocatedQuantity }) => unallocatedQuantity
					)

					if (unallocatedTickets.length) {
						warnings.push(
							`Unable to allocate activity tickets: ${unallocatedTickets
								.map(
									({ ticket, unallocatedQuantity }) =>
										`${unallocatedQuantity} ${ticket.configuration.name}`
								)
								.join(", ")}`
						)
					}
				}
			})
		})

	/**
	 * ---------------------------------------------------------
	 * EXTRAS
	 * ---------------------------------------------------------
	 */

	const allExtras = [...hotel_extras, ...transport_extras, ...other_extras]

	if (allExtras.length) {
		const includeChildrenInExtrasStartAge = includeChildrenInExtras
			? 6
			: undefined

		const includedPaxInExtras = pax.filter((p) => {
			if (
				p.type === "child" &&
				(!includeChildrenInExtrasStartAge ||
					(p.age || 0) < includeChildrenInExtrasStartAge)
			) {
				return false
			}

			return true
		})

		const totalExtrasPrice = allExtras.reduce<Array<TMoney>>((total, extra) => {
			const givenPrice = extra.given_price || 0

			if (!givenPrice) {
				return total
			}

			return addMoneyFromDifferentCurrencies(
				total.concat([moneyParseByDecimal(givenPrice, extra.currency)])
			)
		}, [])

		const perPaxPrice = totalExtrasPrice.map((money) =>
			divideMoneyBy(money, includedPaxInExtras.length || 1, "ceil")
		)

		includedPaxInExtras.forEach((p) => {
			addPriceToPax(p, "extras", perPaxPrice)
		})

		const hotelExtrasPrice = hotel_extras.reduce<Array<TMoney>>(
			(total, extra) => {
				const givenPrice = extra.given_price || 0

				if (!givenPrice) {
					return total
				}

				return addMoneyFromDifferentCurrencies(
					total.concat([moneyParseByDecimal(givenPrice, extra.currency)])
				)
			},
			[]
		)

		const transportExtrasPrice = transport_extras.reduce<Array<TMoney>>(
			(total, extra) => {
				const givenPrice = extra.given_price || 0

				if (!givenPrice) {
					return total
				}

				return addMoneyFromDifferentCurrencies(
					total.concat([moneyParseByDecimal(givenPrice, extra.currency)])
				)
			},
			[]
		)

		const hotelExtrasPerPax = hotelExtrasPrice.map((money) =>
			divideMoneyBy(money, includedPaxInExtras.length || 1, "ceil")
		)

		const includedPaxInTransportExtras =
			cabs.length > 0 ? includedPaxInCabs : includedPaxInExtras

		const transportExtrasPerPax = transportExtrasPrice.map((money) =>
			divideMoneyBy(money, includedPaxInTransportExtras.length || 1, "ceil")
		)

		includedPaxInExtras.forEach((p) => {
			addPriceToPax(p, "hotel_extras", hotelExtrasPerPax)
		})

		includedPaxInTransportExtras.forEach((p) => {
			addPriceToPax(p, "transport_extras", transportExtrasPerPax)
		})
	}

	// ---------------------------------------------------------
	// FLIGHTS
	// ---------------------------------------------------------

	if (flights.length) {
		const totalFlightsPrice = flights.reduce<Array<TMoney>>((total, flight) => {
			const givenPrice = flight.given_price || 0

			if (!givenPrice) {
				return total
			}

			return addMoneyFromDifferentCurrencies(
				total.concat([moneyParseByDecimal(givenPrice, flight.currency)])
			)
		}, [])

		const includedPaxInFlights = pax

		const perPaxPrice = totalFlightsPrice.map((money) =>
			divideMoneyBy(money, includedPaxInFlights.length || 1, "ceil")
		)

		includedPaxInFlights.forEach((p) => {
			addPriceToPax(p, "flights", perPaxPrice)
		})
	}

	// ---------------------------------------------------------
	// VISAS
	// ---------------------------------------------------------

	if (visas?.length) {
		visas.forEach((service) => {
			// Track passengers already allocated for this visa service.
			const allocatedPaxId: Record<number, boolean> = {}
			const result = processVisasServiceForPax({
				service: {
					...service,
					pax: service.quantity,
					given_price: Number(formatMoneyByDecimal(service.given_price_money)),
				},
				pax,
				allocatedPaxId,
				visaCurrency: service.given_price_money.currency,
			})
			if (!result) return

			// Keep allocation failures as warnings instead of stopping
			// the complete passenger allocation process.
			if ("error" in result && result.error) {
				warnings.push(result.error)
				return
			}

			// Mark passengers used by the visa allocation.
			if (result.usedPaxIds) {
				result.usedPaxIds.forEach((id) => {
					allocatedPaxId[id] = true
				})
			}

			// Add the allocated visa price to each passenger.
			if (result.allocations) {
				result.allocations.forEach(({ pax: paxAllocation, price }) => {
					const assignedPax = pax.find(
						(person) => person.id === paxAllocation.id
					)

					if (assignedPax) {
						addPriceToPax(assignedPax, "visas", price)
					}
				})
			}
		})
	}
	/**
	 * ---------------------------------------------------------
	 * SHARING LABEL
	 * ---------------------------------------------------------
	 */

	function getLabelForSharing(sharing: number) {
		switch (sharing) {
			case 1:
				return "Single Sharing"
			case 2:
				return "Double Sharing"
			case 3:
				return "Triple Sharing"
			case 4:
				return "Quad Sharing"
			default:
				return sharing + " pax/room"
		}
	}

	/**
	 * ---------------------------------------------------------
	 * FINAL GROUPING
	 * ---------------------------------------------------------
	 */
	const allocation = collect(pax)
		.groupBy(
			(p) =>
				(p.accommodation || "") +
				"-" +
				p.price.map((t) => `${t.currency}${t.amount}`).join("+") +
				"-" +
				p.type +
				"-" +
				p.room_sharing_type
		)
		.toArray()
		.map((pax, index) => {
			const { type, accommodation, room_sharing_type } = pax[0]

			let perPaxLabel = type === "adult" ? "Adult" : "Child"

			switch (accommodation) {
				case "extra_bed":
					perPaxLabel += " with Extra Bed/Mattress"
					break

				case "no_extra_bed":
					perPaxLabel += " without Extra Bed/Mattress"
					break

				case "base":
					perPaxLabel =
						(type === "adult" ? "Person" : "Child") +
						(room_sharing_type
							? ` (${getLabelForSharing(room_sharing_type)})`
							: "")
					break
			}

			let totalPaxLabel = ""

			switch (type) {
				case "adult":
					totalPaxLabel = pax.length + " Pax"
					break

				case "child":
					totalPaxLabel = `${pluralize("Child", pax.length, true)} (${pax
						.map((p) => p.age)
						.filter(Boolean)
						.join("y, ")}y)`
					break
			}

			return {
				...pax[0],
				id: `${index}_` + perPaxLabel.replace(/[\W]/gi, "_"),
				count: pax.length,
				per_pax_label: perPaxLabel,
				total_pax_label: totalPaxLabel,
				individuals: pax,
			}
		})
		.map(
			({
				price,
				hotels_price,
				cabs_price,
				travel_activities_price,
				flights_price,
				extras_price,
				hotel_extras_price,
				transport_extras_price,
				visas_price,
				...others
			}) => {
				/**
				 * here we are doing
				 */
				const hotels_price_decimal =
					convertMoneyToCostingCurrencyAndGetDecimalAmount(hotels_price)

				const cabs_price_decimal =
					convertMoneyToCostingCurrencyAndGetDecimalAmount(cabs_price)

				const travel_activities_price_decimal =
					convertMoneyToCostingCurrencyAndGetDecimalAmount(
						travel_activities_price
					)

				const flights_price_decimal =
					convertMoneyToCostingCurrencyAndGetDecimalAmount(flights_price)

				const extras_price_decimal =
					convertMoneyToCostingCurrencyAndGetDecimalAmount(extras_price)

				const hotel_extras_price_decimal =
					convertMoneyToCostingCurrencyAndGetDecimalAmount(hotel_extras_price)

				const transport_extras_price_decimal =
					convertMoneyToCostingCurrencyAndGetDecimalAmount(
						transport_extras_price
					)

				const visas_price_decimal =
					convertMoneyToCostingCurrencyAndGetDecimalAmount(visas_price)
				return {
					...others,
					currency: costingCurrency,

					price: addMoney(
						moneyParseByDecimal(hotels_price_decimal, costingCurrency),
						moneyParseByDecimal(cabs_price_decimal, costingCurrency),
						moneyParseByDecimal(
							travel_activities_price_decimal,
							costingCurrency
						),
						moneyParseByDecimal(flights_price_decimal, costingCurrency),
						moneyParseByDecimal(extras_price_decimal, costingCurrency),
						moneyParseByDecimal(visas_price_decimal, costingCurrency)
					),

					hotels_price: hotels_price_decimal,
					cabs_price: cabs_price_decimal,
					travel_activities_price: travel_activities_price_decimal,
					flights_price: flights_price_decimal,
					extras_price: extras_price_decimal,
					hotel_extras_price: hotel_extras_price_decimal,
					transport_extras_price: transport_extras_price_decimal,
					pax_count: others.individuals.length,
					visas_price: visas_price_decimal,
					ages: others.individuals
						.map((p) => p.age)
						.filter((age): age is number => Boolean(age)),
				}
			}
		)

	/**
	 * ---------------------------------------------------------
	 * ROOT LEVEL TOTALS
	 * ---------------------------------------------------------
	 *
	 * The allocation values above are PER-PAX values.
	 *
	 * Therefore:
	 *
	 * root total = per-pax price * number of pax
	 *
	 * All these values are already normalized to costingCurrency.
	 */

	const getExtraTotal = (
		extras: Array<{
			given_price?: number
			currency: string
		}>
	) => {
		const total = extras.reduce<Array<TMoney>>((total, extra) => {
			const givenPrice = extra.given_price || 0

			if (!givenPrice) return total

			return addMoneyFromDifferentCurrencies(
				total.concat([moneyParseByDecimal(givenPrice, extra.currency)])
			)
		}, [])

		return convertMoneyToCostingCurrencyAndGetDecimalAmount(total)
	}

	const rootTotals = allocation.reduce(
		(total, item) => ({
			hotels_price:
				total.hotels_price + Number(item.hotels_price || 0) * item.pax_count,

			cabs_price:
				total.cabs_price + Number(item.cabs_price || 0) * item.pax_count,

			travel_activities_price:
				total.travel_activities_price +
				Number(item.travel_activities_price || 0) * item.pax_count,

			flights_price:
				total.flights_price + Number(item.flights_price || 0) * item.pax_count,

			extras_price:
				total.extras_price + Number(item.extras_price || 0) * item.pax_count,

			visas_price:
				total.visas_price + Number(item.visas_price || 0) * item.pax_count,
		}),
		{
			hotels_price: 0,
			cabs_price: 0,
			travel_activities_price: 0,
			flights_price: 0,
			extras_price: 0,
			visas_price: 0,
		}
	)

	const total_price =
		rootTotals.hotels_price +
		rootTotals.cabs_price +
		rootTotals.travel_activities_price +
		rootTotals.flights_price +
		rootTotals.extras_price +
		rootTotals.visas_price

	const hotel_extras_price = getExtraTotal(hotel_extras)
	const transport_extras_price = getExtraTotal(transport_extras)
	const other_extras_price = getExtraTotal(other_extras)
	return {
		allocation,

		// Root-level totals
		currency: costingCurrency,
		hotels_price: rootTotals.hotels_price,
		cabs_price: rootTotals.cabs_price,
		travel_activities_price: rootTotals.travel_activities_price,
		flights_price: rootTotals.flights_price,
		extras_price: rootTotals.extras_price,
		total_price,
		hotel_extras_price,
		transport_extras_price,
		other_extras_price,
		warnings,
		visas_price: rootTotals.visas_price,
	}
}
